// ===================================================
// LandingScreen.js — 앱 홈 화면 컴포넌트
//
// 역할:
//   - 유저가 오늘의 기록을 짧은 텍스트로 입력
//   - 하단의 모드 토글(2x2 그리드)로 기록 유형 선택
//   - 입력 후 '다음으로' 클릭 → AI 질문 화면으로 전환
//   - 자유 기록 토글: AI 질문 없이 긴 텍스트를 직접 저장
//   - 플래너 모드: 구글 캘린더 미연동 안내 카드 표시
//   - 플래너 모드: 단순 일정 추가 후 한 줄 질문 카드 표시
// ===================================================

import { useState } from 'react';
import './LandingScreen.css';
import { UI_TEXT } from './content/uiText';
import { MODE_CONFIG } from './content/modeConfig';
import { ACTIVE_MODES, FREEWRITE_MODES, FREEWRITE_MAX_LENGTH } from './constants';

// 카운터를 표시하기 시작하는 글자 수 (이 미만이면 카운터 숨김) — AI 질문 플로우용
const COUNTER_SHOW_AT = 100;
// 권장 글자 수 — 초과 시 카운터 색상이 조금 더 진해짐 (강제 차단 없음)
const SOFT_LIMIT = 150;

// 아직 유저에게 제공하지 않는 모드 (선택 시 출시 준비 중 안내 표시)
const COMING_SOON_MODES = [];

function LandingScreen({
  mode, onModeChange, onNext, onFreewriteSave, loading, error,
  // 구글 캘린더 연동 관련 props
  showCalendarPrompt, onDismissCalendarPrompt, onConnectCalendar,
  quickQuestion, onQuickQuestionSave, onQuickQuestionDismiss,
}) {
  const T = UI_TEXT.landing;
  const [text, setText] = useState('');
  // 출시 준비 중 모달 표시 여부
  const [showComingSoon, setShowComingSoon] = useState(false);
  // 자유 기록 모드 토글
  const [freewrite, setFreewrite] = useState(false);
  // 자유 기록 제목 (선택 입력, 최대 30자)
  const [freewriteTitle, setFreewriteTitle] = useState('');
  // 한 줄 질문 답변 입력
  const [quickAnswer, setQuickAnswer] = useState('');
  const config = MODE_CONFIG[mode];

  // 현재 모드가 자유 기록을 지원하는지
  const canFreewrite = FREEWRITE_MODES.includes(mode);
  // 실제 자유 기록 활성 상태
  const isFreewrite = freewrite && canFreewrite;

  // 텍스트 영역 자동 높이 조절 (내용에 맞춰 확장)
  const autoResize = (el) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 400) + 'px';
  };

  const handleChange = (e) => {
    // 자유 기록 모드: 5000자 상한, 일반 모드: 소프트 가이드만 (강제 차단 없음)
    if (isFreewrite && e.target.value.length > FREEWRITE_MAX_LENGTH) return;
    setText(e.target.value);
    autoResize(e.target);
  };

  const handleKeyDown = (e) => {
    // 자유 기록 모드에서는 Enter로 줄바꿈 허용 (제출하지 않음)
    if (isFreewrite) return;
    if (e.key === 'Enter' && !e.shiftKey && text.length > 0 && !loading) {
      e.preventDefault();
      onNext(text, mode);
    }
  };

  // 모드 버튼 클릭: 미출시 모드면 안내 모달, 아니면 모드 전환
  const handleModeClick = (m) => {
    if (COMING_SOON_MODES.includes(m)) {
      setShowComingSoon(true);
    } else {
      onModeChange(m);
      // 자유 기록을 지원하지 않는 모드로 전환 시 토글 해제
      if (!FREEWRITE_MODES.includes(m)) {
        setFreewrite(false);
      }
    }
  };

  // 자유 기록 토글 변경 — 본문과 제목을 유지한 채 전환
  const handleFreewriteToggle = () => {
    setFreewrite(prev => !prev);
  };

  // 제목 입력 핸들러 (최대 30자)
  const handleTitleChange = (e) => {
    if (e.target.value.length <= 50) setFreewriteTitle(e.target.value);
  };

  // 제출 핸들러: 자유 기록이면 직접 저장, 아니면 AI 질문 플로우
  const handleSubmit = () => {
    if (text.length === 0 || loading) return;
    if (isFreewrite) {
      // 제목이 비어있으면 null 전달 (AI 자동 생성 또는 날짜 표시)
      const title = freewriteTitle.trim() || null;
      onFreewriteSave(text, mode, title);
      setText('');
      setFreewriteTitle('');
    } else {
      onNext(text, mode);
    }
  };

  // 캘린더 미연동 안내에서 "괜찮아요" 클릭 → 기존 Q&A 플로우 진행
  const handleCalendarPromptDismiss = () => {
    onDismissCalendarPrompt();
    onNext(text, mode);
  };

  // 한 줄 질문 답변 제출
  const handleQuickSubmit = () => {
    if (!quickAnswer.trim()) return;
    onQuickQuestionSave(quickQuestion.question, quickAnswer.trim());
    setQuickAnswer('');
  };

  return (
    <div className={`main-content ${mode}`}>

      {/* 출시 준비 중 안내 모달 */}
      {showComingSoon && (
        <div className="coming-soon-overlay" onClick={() => setShowComingSoon(false)}>
          <div className="coming-soon-box" onClick={e => e.stopPropagation()}>
            <p className="coming-soon-emoji">🛠️</p>
            <p className="coming-soon-title">출시 준비 중입니다</p>
            <p className="coming-soon-desc">더 나은 경험으로 곧 찾아올게요!</p>
            <button className="coming-soon-btn" onClick={() => setShowComingSoon(false)}>
              확인
            </button>
          </div>
        </div>
      )}

      {/* ── 구글 캘린더 미연동 안내 카드 ── */}
      {showCalendarPrompt && (
        <div className="coming-soon-overlay" onClick={handleCalendarPromptDismiss}>
          <div className="coming-soon-box calendar-prompt-box" onClick={e => e.stopPropagation()}>
            <p className="coming-soon-emoji">📅</p>
            <p className="coming-soon-title">구글 캘린더 연동</p>
            <p className="coming-soon-desc">
              구글 계정을 연결하면 구글 캘린더에<br />일정이 자동으로 추가돼요
            </p>
            <button className="coming-soon-btn" onClick={onConnectCalendar}>
              구글 계정 연결하기
            </button>
            <button
              className="calendar-prompt-dismiss"
              onClick={handleCalendarPromptDismiss}
            >
              괜찮아요, 그냥 기록할게요
            </button>
          </div>
        </div>
      )}

      {/* ── 단순 일정 추가 후 한 줄 질문 카드 ── */}
      {quickQuestion && (
        <div className="quick-question-card">
          <p className="quick-question-text">💬 {quickQuestion.question}</p>
          <div className="quick-question-input-row">
            <input
              className="quick-question-input"
              type="text"
              placeholder="한 줄 답변..."
              value={quickAnswer}
              onChange={e => setQuickAnswer(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleQuickSubmit(); }}
              autoFocus
            />
          </div>
          <div className="quick-question-actions">
            <button
              className="quick-question-submit"
              onClick={handleQuickSubmit}
              disabled={!quickAnswer.trim()}
            >
              답변하기
            </button>
            <button
              className="quick-question-dismiss"
              onClick={() => { onQuickQuestionDismiss(); setQuickAnswer(''); }}
            >
              괜찮아요
            </button>
          </div>
        </div>
      )}

      <div className="landing-card">

        <div className="icon-circle">{config.icon}</div>
        <h1 className="landing-title">{config.title}</h1>
        <p className="landing-subtitle">
          {isFreewrite ? config.freewriteSubtitle : config.subtitle}
        </p>

        <div className={`textarea-wrapper glass-card ${isFreewrite ? 'freewrite' : ''}`}>
          {/* 자유 기록: 제목 입력 필드 (선택) */}
          {isFreewrite && (
            <input
              className="freewrite-title-input"
              type="text"
              placeholder="제목 없음"
              value={freewriteTitle}
              onChange={handleTitleChange}
              disabled={loading}
            />
          )}
          <textarea
            className={`landing-textarea ${isFreewrite ? 'freewrite-textarea' : ''}`}
            placeholder={isFreewrite ? config.freewritePlaceholder : config.placeholder}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          {/* 자유 기록: 항상 글자 수 표시, 일반 모드: 100자 넘으면 표시 (소프트 가이드) */}
          {isFreewrite
            ? <span className="char-count">{text.length}/{FREEWRITE_MAX_LENGTH}</span>
            : text.length >= COUNTER_SHOW_AT && (
                <span className={`char-count${text.length > SOFT_LIMIT ? ' char-count--over' : ''}`}>
                  {text.length}
                </span>
              )
          }
        </div>

        {error && <p className="error-message">{error}</p>}

        {/* 자유 기록 토글 — 지원 모드(다이어리/노트)에서만 보이고, 나머지는 자리만 차지 */}
        <label className={`freewrite-toggle${!canFreewrite ? ' freewrite-toggle--invisible' : ''}`}>
          {/* 숨긴 체크박스: 상태 관리용 */}
          <input
            type="checkbox"
            checked={freewrite}
            onChange={handleFreewriteToggle}
            className="freewrite-checkbox"
            tabIndex={canFreewrite ? 0 : -1}
          />
          {/* 시각적 토글 스위치 */}
          <span className="freewrite-switch-track">
            <span className="freewrite-switch-thumb"></span>
          </span>
          <span className="freewrite-toggle-label">{T.freewriteLabel}</span>
        </label>

        <button
          className={`next-button ${text.length > 0 && !loading ? 'active' : ''}`}
          disabled={text.length === 0 || loading}
          onClick={handleSubmit}
        >
          {loading
            ? (isFreewrite ? T.saving : T.analyzing)
            : (isFreewrite ? T.saveBtn : T.nextBtn)
          }
        </button>

        <div className="mode-toggle">
          {ACTIVE_MODES.map((m) => (
            <button
              key={m}
              className={`mode-toggle-btn ${mode === m ? 'active' : ''}`}
              onClick={() => handleModeClick(m)}
            >
              {MODE_CONFIG[m].icon} {MODE_CONFIG[m].label}
            </button>
          ))}
        </div>

      </div>

    </div>
  );
}

export default LandingScreen;
